import React, { useCallback, useEffect, useRef, useState, useMemo } from "react"
import { motion } from "framer-motion"
import Lightbox from "yet-another-react-lightbox"
import Zoom from "yet-another-react-lightbox/plugins/zoom"
import "yet-another-react-lightbox/styles.css"
import { apiGet } from "../api"
import serverName from "../serverName"

const CHANNEL_ID = "1325502791183110245"

function useInfinite(cb) {
    const ref = useRef(null)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const io = new IntersectionObserver(e => { if (e[0].isIntersecting) cb() }, { rootMargin: "600px" })
        io.observe(el)
        return () => io.disconnect()
    }, [cb])
    return ref
}

export default function Gallery() {
    const [thumbs, setThumbs] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [cursor, setCursor] = useState(null)
    const [hasMore, setHasMore] = useState(true)
    const [isFetching, setIsFetching] = useState(false)
    const [open, setOpen] = useState(false)
    const [index, setIndex] = useState(0)
    const seenIds = useRef(new Set())

    const load = useCallback(async () => {
        if (!hasMore || isFetching) return
        setIsFetching(true)
        try {
            const qs = new URLSearchParams()
            qs.set("channel", CHANNEL_ID)
            qs.set("limit", "60")
            if (cursor) qs.set("before", cursor)
            const r = await apiGet(`/discord_gallery.php?${qs.toString()}&ts=${Date.now()}`)
            if (!r?.ok) throw new Error()
            const raw = Array.isArray(r.items) ? r.items : []
            const unique = raw.filter(it => it?.id && !seenIds.current.has(it.id))
            unique.forEach(it => seenIds.current.add(it.id))
            if (unique.length) {
                const mapped = unique.map(u => ({
                    id: u.id,
                    src: u.url,
                    alt: u.alt || "image",
                    author: (u.author && u.author.name) || "",
                    ts: u.timestamp || 0
                }))
                setThumbs(prev => prev.concat(mapped))
            }
            setCursor(r.next_before || null)
            setHasMore(Boolean(r.has_more) && raw.length > 0)
            setLoading(false)
        } catch {
            setError("Načítání selhalo")
            setLoading(false)
        } finally {
            setIsFetching(false)
        }
    }, [cursor, hasMore, isFetching])

    useEffect(() => { load() }, [])
    const sentinelRef = useInfinite(load)

    const slides = useMemo(() => thumbs.map(t => ({
        src: t.src,
        alt: t.alt,
        description: t.author ? t.author : ""
    })), [thumbs])

    return (
        <div className="max-w-6xl mx-auto px-6 py-20">
            <motion.h1
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="text-center text-5xl font-extrabold mb-10 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
            >
                Galerie {serverName}
            </motion.h1>

            {error ? <div className="text-center text-red-300 mb-6">{error}</div> : null}
            <div className="relative overflow-hidden rounded-2xl p-[1px] mb-8 bg-gradient-to-r from-purple-500/40 via-fuchsia-500/30 to-indigo-500/40">
                <div className="relative rounded-2xl bg-black/40 backdrop-blur-md px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 text-purple-300 text-2xl">📸</div>
                    <div className="text-white/90 leading-relaxed">
                        Tyto snímky pořídili hráči komunity <span className="font-semibold text-white">{serverName}</span> a byly automaticky nahrány z našeho Discord serveru. Děkujeme všem, kteří se podílejí na sdílení těchto momentů!
                    </div>
                </div>
            </div>


            {loading && thumbs.length === 0 ? (
                <div className="flex items-center justify-center min-h-[40vh]">
                    <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.2 }} className="text-white/70 text-xl">Načítám…</motion.div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {thumbs.map((t, i) => (
                            <button
                                key={t.id}
                                onClick={() => { setIndex(i); setOpen(true) }}
                                className="group relative w-full aspect-[4/3] overflow-hidden rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400/60"
                                title={t.author || ""}
                            >
                                <img
                                    src={t.src}
                                    alt={t.alt}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover block transition-transform duration-200 group-hover:scale-[1.02]"
                                />
                                {t.author ? (
                                    <div className="absolute left-1.5 bottom-1.5 text-[11px] leading-none text-white/95 bg-black/65 rounded px-1.5 py-1">
                                        {t.author}
                                    </div>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {hasMore ? <div ref={sentinelRef} className="h-12" /> : null}
                </>
            )}

            <Lightbox
                open={open}
                close={() => setOpen(false)}
                index={index}
                slides={slides}
                plugins={[Zoom]}
                animation={{ fade: 220 }}
                carousel={{ finite: false }}
                styles={{ container: { backgroundColor: "rgba(0,0,0,0.9)" } }}
                render={{
                    description: ({ slide }) =>
                        slide.description ? (
                            <div className="text-2xl font-semibold text-white/95 mt-3">
                                {slide.description}
                            </div>
                        ) : null
                }}
            />
        </div>
    )
}
